import { memo, useCallback } from "react";
import carImage from "../../../assets/images/car.webp";
import lineAsset from "../../../assets/images/line.png";
import bonnetIcon from "../../../assets/images/door-icons/bonnet.png";
import trunkIcon from "../../../assets/images/door-icons/trunk.png";
import leftDoorIcon from "../../../assets/images/door-icons/left-door.png";
import rightDoorIcon from "../../../assets/images/door-icons/right-door.png";
import ActionButtons from "./ActionButtons";
import ToggleSwitcher from "../shared/ToggleSwitcher";
import type { Door, CarPanel as PanelType } from "../types";

interface VehicleVisualizerProps {
  doors: Door[];
  className?: string;
  panels: PanelType[];
  onDoorToggle: (id: Door["id"]) => void;
  onPanelToggle: (id: PanelType["id"]) => void;
  onOpenAll: () => void;
  onCloseAll: () => void;
}

const DOOR_POSITION_CLASS: Record<Door["id"], string> = {
  fl: "top-[34%] left-[6%]",
  fr: "top-[34%] right-[6%]",
  rl: "top-[60%] left-[6%]",
  rr: "top-[60%] right-[6%]",
};

const PanelIconButton = ({
  icon,
  isOpen,
  onClick,
  title,
}: {
  icon: string;
  isOpen: boolean;
  onClick: () => void;
  title: string;
}) => (
  <button
    onClick={onClick}
    title={title}
    className={`w-[33px] h-[33px] rounded-[6px] flex items-center justify-center border transition-colors
      ${isOpen ? "bg-[#0d1a27] border-white/10" : "bg-[#1a0e0e] border-red-900/40"}`}
  >
    <img src={icon} alt={title} className="w-[18px] h-[18px] object-contain" />
  </button>
);

const VehicleVisualizer = memo<VehicleVisualizerProps>(
  ({
    doors,
    panels,
    onDoorToggle,
    onPanelToggle,
    onOpenAll,
    onCloseAll,
    className = "",
  }) => {
    const isLeft = useCallback(
      (id: Door["id"]) => id === "fl" || id === "rl",
      [],
    );

    const hood = panels.find((p) => p.id === "hood");
    const trunk = panels.find((p) => p.id === "trunk");

    return (
      <div
        className={`flex-1 min-w-0 bg-[#0c0b0b]/60 border border-white/[0.06] rounded-xl overflow-hidden flex flex-col ${className}`}
      >
        <div className="relative flex-1 flex items-center justify-center py-2">
          <img
            src={carImage}
            alt="Vehicle top-down view"
            draggable={false}
            className="h-full max-h-[258px] w-auto object-contain select-none pointer-events-none"
          />

          {/* Hood */}
          {hood && (
            <div className="absolute top-[5%] left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5">
              <ToggleSwitcher
                isOn={hood.isOpen}
                onClick={() => onPanelToggle("hood")}
              />
              <PanelIconButton
                icon={bonnetIcon}
                isOpen={hood.isOpen}
                onClick={() => onPanelToggle("hood")}
                title="Hood"
              />
            </div>
          )}

          {/* Trunk */}
          {trunk && (
            <div className="absolute bottom-[9%] left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5">
              <ToggleSwitcher
                isOn={trunk.isOpen}
                onClick={() => onPanelToggle("trunk")}
              />
              <PanelIconButton
                icon={trunkIcon}
                isOpen={trunk.isOpen}
                onClick={() => onPanelToggle("trunk")}
                title="Trunk"
              />
            </div>
          )}

          {/* Doors */}
          {doors.map((door) => {
            const left = isLeft(door.id);
            const icon = left ? leftDoorIcon : rightDoorIcon;

            const iconBtn = (
              <PanelIconButton
                icon={icon}
                isOpen={door.isOpen}
                onClick={() => onDoorToggle(door.id)}
                title={`${door.label} door`}
              />
            );

            const switcher = (
              <ToggleSwitcher
                isOn={door.isOpen}
                onClick={() => onDoorToggle(door.id)}
                vertical
              />
            );

            const line = (
              <div
                className={`w-[100px] h-[2px] bg-gradient-to-r ${left ? "from-white/0 to-white/20" : "from-white/20 to-white/0"}`}
              ></div>
            );

            return (
              <div
                key={door.id}
                className={`absolute flex items-center gap-1 ${DOOR_POSITION_CLASS[door.id]}`}
              >
                {left ? (
                  <>
                    {switcher}
                    {iconBtn}
                    {line}
                  </>
                ) : (
                  <>
                    {line}
                    {iconBtn}
                    {switcher}
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-4 pb-4 pt-1">
          <ActionButtons onOpenAll={onOpenAll} onCloseAll={onCloseAll} />
        </div>
      </div>
    );
  },
);

VehicleVisualizer.displayName = "VehicleVisualizer";
export default VehicleVisualizer;
